import React, { useCallback } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  API_URL_HOTSPOT_LANE_LABELS,
  API_URL_HOTSPOT_LANE_TYPES,
  nextAvailableApiUrlLaneType,
  type ApiUrlHotspotEntry,
  type ApiUrlHotspotLaneType,
} from "../../api/cameraHotspots.ts";

type AdminApiUrlHotspotFieldsProps = {
  entries: ApiUrlHotspotEntry[];
  onChange: (entries: ApiUrlHotspotEntry[]) => void;
};

function newEntryId(): string {
  return `lane-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function AdminApiUrlHotspotFields({
  entries,
  onChange,
}: AdminApiUrlHotspotFieldsProps) {
  const canAddMore = nextAvailableApiUrlLaneType(entries) != null;

  const updateRow = useCallback(
    (id: string, patch: Partial<Pick<ApiUrlHotspotEntry, "type" | "url">>) => {
      onChange(
        entries.map((row) => (row.id === id ? { ...row, ...patch } : row)),
      );
    },
    [entries, onChange],
  );

  const removeRow = useCallback(
    (id: string) => {
      if (entries.length <= 1) return;
      onChange(entries.filter((row) => row.id !== id));
    },
    [entries, onChange],
  );

  const addRow = useCallback(() => {
    const nextType = nextAvailableApiUrlLaneType(entries);
    if (!nextType) return;
    onChange([...entries, { id: newEntryId(), type: nextType, url: "" }]);
  }, [entries, onChange]);

  return (
    <Box sx={{ gridColumn: { sm: "1 / -1" }, display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Add one or more API URLs. Choose a signal type (green, red, or yellow) for
        each; patrol shows matching buttons on the hotspot.
      </Typography>

      {entries.map((row, index) => (
        <Box
          key={row.id}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 140px auto" },
            gap: 1,
            alignItems: "flex-start",
          }}
        >
          <TextField
            label={`API URL ${index + 1}`}
            size="small"
            required
            fullWidth
            value={row.url}
            onChange={(e) => updateRow(row.id, { url: e.target.value })}
            placeholder="https://api.example.com/endpoint"
          />
          <FormControl size="small" required>
            <InputLabel id={`api-url-type-${row.id}`}>URL type</InputLabel>
            <Select
              labelId={`api-url-type-${row.id}`}
              label="URL type"
              value={row.type}
              onChange={(e) =>
                updateRow(row.id, {
                  type: e.target.value as ApiUrlHotspotLaneType,
                })
              }
            >
              {API_URL_HOTSPOT_LANE_TYPES.map((t) => {
                const taken = entries.some(
                  (r) => r.id !== row.id && r.type === t,
                );
                return (
                  <MenuItem key={t} value={t} disabled={taken}>
                    {API_URL_HOTSPOT_LANE_LABELS[t]}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          <IconButton
            size="small"
            aria-label="Remove API URL"
            onClick={() => removeRow(row.id)}
            disabled={entries.length <= 1}
            sx={{ mt: { xs: 0, sm: 0.5 } }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addRow}
        disabled={!canAddMore}
        sx={{ alignSelf: "flex-start" }}
      >
        Add another API URL
      </Button>
    </Box>
  );
}
